import React, { useState, useEffect } from "react";
import { ref, onValue } from 'firebase/database';
import { database } from '../firebaseConfig';
import "./EmailAiPopup.css";

const EmailAiPopupTopics = ({ 
  selectedTopic, 
  setSelectedTopic, 
  rowData, 
  topicTypeMapping,
  source = "", // Neuer Parameter
  userId
}) => {
  const [userTopics, setUserTopics] = useState({});
  
  // Lade die benutzerdefinierten Topics
  useEffect(() => {
    if (!userId) return;
    
    const userTopicsRef = ref(database, `/World/${userId}/settings/selectedTopics`);
    const unsubscribe = onValue(userTopicsRef, (snapshot) => {
      if (snapshot.exists()) {
        setUserTopics(snapshot.val());
      }
    });
    
    return () => unsubscribe();
  }, [userId]);
  
  // Funktion zum Abrufen der verfügbaren Topics basierend auf der Quelle
  const getAvailableTopics = () => {
    // Wenn das Popup von der Inbox aufgerufen wurde, zeige die Inbox-spezifischen Topics
    if (source === "inbox") {
      // Prüfe, ob benutzerdefinierte Reply-Topics vorhanden sind
      const hasCustomReplyTopics = Object.values(userTopics).some(topic => 
        ['reply_1', 'reply_2', 'reply_3', 'reply_4', 'reply_5', 'reply_6'].includes(topic.type)
      );
      
      if (hasCustomReplyTopics) {
        // Erstelle eine Liste der benutzerdefinierten Reply-Topics
        const customReplyTopics = [];
        
        // Suche nach benutzerdefinierten Topics für jeden Reply-Typ
        ['reply_1', 'reply_2', 'reply_3', 'reply_4', 'reply_5', 'reply_6'].forEach(replyType => {
          const customTopic = Object.entries(userTopics).find(([_, data]) => data.type === replyType);
          if (customTopic) {
            customReplyTopics.push(customTopic[1].topic);
          }
        });
        
        return customReplyTopics.length > 0 ? customReplyTopics : [
          "Reply To Mail",
          "Send Details",
          "Schedule Zoom Call",
          "Ask for Meetup",
          "Featured Article",
          "Social Proof"
        ];
      } else {
        // Fallback zu den Standard-Topics
        return [
          "Reply To Mail",
          "Send Details",
          "Schedule Zoom Call",
          "Ask for Meetup",
          "Featured Article",
          "Social Proof"
        ];
      }
    }
    
    // Wenn es eine normale Antwort ist (aber nicht von der Inbox)
    if (source === "reply") {
      return [
        "Freundliche Antwort", 
        "Professionelle Antwort", 
        "Detaillierte Antwort", 
        "Kurze Antwort"
      ];
    }
    
    // Für normale E-Mails geben wir immer alle zurück, damit sie angezeigt werden können
    return ["Intro", "1. Follow Up", "2. Follow Up", "3. Follow Up"];
  };
  
  // Hilfsfunktion zur Bestimmung des nächsten Follow-up-Schritts
  const getNextFollowUpStep = (currentStatus) => {
    switch (currentStatus) {
      case 'Introduced':
        return "1. Follow Up";
      case 'First Follow-up':
        return "2. Follow Up";
      case 'Second Follow-up':
        return "3. Follow Up";
      case 'Third Follow-up':
        return null; // Alle Follow-ups wurden bereits gesendet
      default:
        return "Intro";
    }
  };
  
  // Mapping für die Anzeigenamen zu den internen Original-Namen
  const displayToOriginalNames = {
    "Intro": "Introduce Yourself",
    "1. Follow Up": "Follow Up",
    "2. Follow Up": "Second Follow Up",
    "3. Follow Up": "Third Follow Up"
  };
  
  // Mapping für den Vergleich und Topic-Auswahl
  const originalToDisplayNames = {
    "Introduce Yourself": "Intro",
    "Follow Up": "1. Follow Up",
    "Second Follow Up": "2. Follow Up",
    "Third Follow Up": "3. Follow Up"
  };
  
  // Prüft, ob ein Topic für den aktuellen Status verfügbar ist
  const isTopicAvailable = (topic) => {
    // Wenn wir von der Inbox kommen oder es eine Antwort ist, sind alle Topics verfügbar
    if (source === "inbox" || source === "reply") {
      return true;
    }
    
    // Konvertiere den Display-Namen zurück zum Original-Namen für den Vergleich
    const originalTopic = displayToOriginalNames[topic] || topic;
    
    // Für normale E-Mails prüfen wir den Status
    const nextFollowUp = getNextFollowUpStep(rowData.lead_contacted);
    
    // Wenn kein nächstes Follow-up verfügbar ist, zeigen wir "Intro" an
    if (!nextFollowUp) {
      return topic === "Intro";
    }
    
    // Sonst ist nur das nächste Follow-up verfügbar
    return topic === nextFollowUp;
  };
  
  const availableTopics = getAvailableTopics();

  const getActiveStepIndex = () => {
    if (source === "inbox" || source === "reply") return -1;
    const nextStep = getNextFollowUpStep(rowData.lead_contacted);
    if (!nextStep) return availableTopics.length;
    return availableTopics.indexOf(nextStep);
  };

  const activeStepIndex = getActiveStepIndex();
  const progressPercent = activeStepIndex > 0
    ? (activeStepIndex / (availableTopics.length - 1)) * 100
    : 0;

  return (
    <div className="emailAiPopupTopicSection">
      <div className="topicCircleRow">
        <div className="topicCircleConnectingLine" />
        <div
          className="topicCircleConnectingLineProgress"
          style={{ width: `${progressPercent}%` }}
        />
        {availableTopics.map((topic, index) => {
          const displayTopic = source !== "inbox" && source !== "reply" && originalToDisplayNames[topic] ? 
                              originalToDisplayNames[topic] : topic;
          const isSelected = selectedTopic === topic;
          const isCompleted = source !== "inbox" && source !== "reply" && index < activeStepIndex;
          
          return (
            <button
              key={index}
              className={`topicCircleItem ${isSelected ? 'active' : ''} ${isCompleted ? 'completed' : ''} ${!isTopicAvailable(displayTopic) ? 'disabled' : ''}`}
              onClick={() => isTopicAvailable(displayTopic) && setSelectedTopic(topic)}
              disabled={!isTopicAvailable(displayTopic)}
            >
              <span className="topicCircleNumber">
                {isCompleted ? (
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                ) : index + 1}
              </span>
              <span className="topicCircleLabel">{displayTopic}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default EmailAiPopupTopics; 